/**
 * DiaryEntry — a single reflection, rendered in the YUO glow/stone material.
 *
 * Strict rules (matches Diary / Mirror / Symbiosis):
 *   • var(--yuo-bg) background only. No white surfaces, no hard borders.
 *   • Rounded 22–999 corners. Content sits inside a glowing purple capsule.
 *   • Buttons are pill capsules via capsuleStyle(). Reflect modal reuses the
 *     same glow surface — no legacy bright-purple rectangle any more.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  BG, INK, MUTED_36, MUTED_60, FONT as YFONT,
  ROOMS, glow, capsuleStyle,
} from "@/lib/yuoDesign";
import { api } from "@/lib/api";
import { MyNotesSheet, BookIcon } from "@/components/MyNotesSheet";
import { HighlightedText } from "@/components/HighlightedText";
import { HighlightToolbar } from "@/components/HighlightToolbar";
import StoneOrb from "@/components/yuo/StoneOrb";
import { useResponsive } from "@/lib/responsive";

const DIARY_RGB = ROOMS.diary.rgb;         // "168,85,247"
const GROWTH_RGB = ROOMS.growth.rgb;       // "138,255,196"

/** Split reflection into { text | question } blocks. */
function splitReflectionIntoBlocks(text) {
  if (!text) return [];
  const paragraphs = text.split(/\n{2,}/);
  const blocks = [];
  for (const p of paragraphs) {
    const trimmed = p.trim();
    if (!trimmed) continue;
    if (trimmed.endsWith("?") || /\?\s*[»"']?\s*$/.test(trimmed)) {
      blocks.push({ type: "question", text: trimmed });
    } else {
      blocks.push({ type: "text", text: trimmed });
    }
  }
  return blocks;
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return (
      d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
      " · " +
      d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
    );
  } catch { return ""; }
}

export default function DiaryEntry() {
  const { isPhone } = useResponsive();
  const { id } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reflecting, setReflecting] = useState(false);
  const [continueText, setContinueText] = useState("");
  const [saving, setSaving] = useState(false);
  const [showMyNotes, setShowMyNotes] = useState(false);
  const [answerFor, setAnswerFor] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  const editRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const e = await api.getDiary(id);
        setEntry(e);
      } catch (err) {
        console.warn("diary entry load", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const startEditing = () => {
    if (!entry) return;
    setEditDraft(entry.content || "");
    setIsEditing(true);
  };
  const cancelEditing = () => { setIsEditing(false); setEditDraft(""); };
  const saveEditing = async () => {
    if (!entry || editSaving) return;
    const next = editDraft.trim();
    if (!next) return;
    setEditSaving(true);
    try {
      const updated = await api.updateDiary(id, { content: next });
      setEntry(updated);
      setIsEditing(false);
      setEditDraft("");
    } catch (e) {
      alert(e?.message || "Could not save");
    } finally {
      setEditSaving(false);
    }
  };

  const saveContinued = async () => {
    const extra = continueText.trim();
    if (!extra || !entry) return;
    setSaving(true);
    try {
      const nextContent = `${entry.content}\n\n${extra}`;
      const updated = await api.updateDiary(id, { content: nextContent });
      setEntry(updated);
      setContinueText("");
    } catch (e) {
      alert(e?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const submitAnswer = async () => {
    if (!answerFor?.draft?.trim()) return;
    setSaving(true);
    try {
      const stamped =
        `[[coral:${answerFor.question}]]\n` +
        `[[green:answer]] ${answerFor.draft.trim()}`;
      const nextContent = `${entry.content}\n\n${stamped}`;
      const updated = await api.updateDiary(id, { content: nextContent });
      setEntry(updated);
      setAnswerFor(null);
    } catch (e) {
      alert(e?.message || "Could not save answer");
    } finally {
      setSaving(false);
    }
  };

  const runReflection = async () => {
    setReflecting(true);
    try {
      const r = await api.reflectDiary(id);
      setEntry(r);
    } catch (e) {
      alert(e?.message || "Reflection failed");
    } finally {
      setReflecting(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this entry?")) return;
    await api.deleteDiary(id);
    navigate("/diary");
  };

  const insertToContinue = (snippet) => {
    setContinueText((prev) => (prev.trim() ? `${prev.trim()}\n\n${snippet}` : snippet));
  };

  return (
    <div
      data-testid="diary-entry-screen"
      style={{
        position: "fixed", inset: 0,
        background: BG, color: INK, fontFamily: YFONT,
        display: "flex", flexDirection: "column",
      }}>
      {/* Ambient purple wash — matches Diary room */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background:
          `radial-gradient(60% 45% at 18% 4%, rgba(${DIARY_RGB},0.14), transparent 72%),` +
          `radial-gradient(55% 40% at 82% 6%, rgba(var(--role-green-rgb),0.08), transparent 72%),` +
          `radial-gradient(90% 55% at 50% 112%, rgba(${DIARY_RGB},0.12), transparent 72%)`,
      }} />

      {/* Header — small stone + back / actions */}
      <div style={{
        position: "relative", flex: "0 0 auto",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isPhone ? "6px 18px 10px" : "14px 24px 8px", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
          <button
            onClick={() => navigate("/diary")}
            data-testid="diary-entry-back"
            style={isPhone ? {
              display: "inline-flex", alignItems: "center", gap: 8, height: 38, padding: "0 14px", borderRadius: 999,
              background: "var(--m-more-bg)", border: "none", cursor: "pointer", color: INK, fontFamily: YFONT,
              fontSize: 9.5, letterSpacing: "0.2em", textTransform: "uppercase", boxShadow: "none",
            } : {
              display: "inline-flex", alignItems: "center", gap: 10,
              background: "transparent", border: "none", padding: 0, cursor: "pointer",
              color: MUTED_60, fontFamily: YFONT, fontSize: 11,
              letterSpacing: "0.24em", textTransform: "uppercase",
            }}>
            <StoneOrb color="diary" size={12} />
            <span>{isPhone ? "← Diary" : "Back"}</span>
          </button>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!isEditing && entry && (
            <button
              onClick={startEditing}
              data-testid="entry-edit-btn"
              style={capsuleStyle({ active: false, size: "sm", room: "diary" })}>
              Edit
            </button>
          )}
          <button
            onClick={remove}
            data-testid="entry-delete-btn"
            style={{
              ...capsuleStyle({ active: false, size: "sm" }),
              color: "var(--lit-coral-11)",
            }}>
            Delete
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <main className={isPhone ? "m-scroll" : undefined} style={{
        position: "relative", flex: "1 1 0", minHeight: 0, overflow: "auto",
        padding: isPhone ? "6px 14px calc(30px + env(safe-area-inset-bottom, 0px))" : "10px 24px 40px",
      }}>
        {loading ? (
          <div style={{
            color: MUTED_36, fontSize: 11, letterSpacing: "0.32em",
            textAlign: "center", padding: 40, textTransform: "uppercase",
          }}>opening…</div>
        ) : !entry ? (
          <div style={{
            color: MUTED_36, fontSize: 11, letterSpacing: "0.32em",
            textAlign: "center", padding: 40, textTransform: "uppercase",
          }}>not found</div>
        ) : (
          <div style={{
            maxWidth: 780, margin: "0 auto",
            animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
          }}>
            <div style={{
              color: MUTED_36, fontSize: 9.5,
              letterSpacing: "0.2em", textTransform: "uppercase",
              marginBottom: 14, textAlign: "center",
            }}>
              {formatDate(entry.created_at)}
            </div>

            {/* Entry body — glowing purple capsule */}
            <div style={{
              padding: isPhone ? "16px 14px 14px" : "26px 26px 22px",
              borderRadius: isPhone ? 30 : 28,
              background: glow(DIARY_RGB, 0.14),
              boxShadow: `0 0 44px rgba(${DIARY_RGB},0.10) inset`,
              marginBottom: 22,
            }}>
              {isEditing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <textarea
                    ref={editRef}
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    autoFocus
                    data-testid="entry-edit-textarea"
                    placeholder="Select any word or phrase, then tap feeling · craft · growth to color it."
                    style={{
                      width: "100%", minHeight: isPhone ? 250 : 220,
                      background: isPhone ? "rgba(40,28,60,0.55)" : "var(--fg-04)",
                      border: "none", outline: "none",
                      borderRadius: 22, padding: isPhone ? 14 : 18,
                      color: INK, fontFamily: YFONT,
                      fontSize: isPhone ? 13 : 15, lineHeight: 1.7,
                      boxShadow: `0 0 44px rgba(${DIARY_RGB},0.18) inset`,
                    }}
                  />
                  <HighlightToolbar
                    textareaRef={editRef}
                    value={editDraft}
                    onChange={setEditDraft}
                    testIdPrefix="entry-edit"
                  />
                  {isPhone && (
                    <div data-testid="entry-edit-preview" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ color: MUTED_36, fontSize: 8.5, letterSpacing: "0.24em", textTransform: "uppercase" }}>How it reads in the diary</div>
                      <div style={{ color: INK, fontSize: 12.5, lineHeight: 1.65, display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        <HighlightedText content={editDraft} />
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <button
                      onClick={cancelEditing}
                      data-testid="entry-edit-cancel"
                      style={{ ...capsuleStyle({ active: false, size: "sm" }), ...(isPhone ? { height: 44, display: "inline-flex", alignItems: "center" } : {}) }}>
                      Cancel
                    </button>
                    <button
                      onClick={saveEditing}
                      disabled={editSaving || !editDraft.trim()}
                      data-testid="entry-edit-save"
                      style={{
                        ...capsuleStyle({ active: true, size: "md", room: "diary" }),
                        flex: 1,
                        ...(isPhone ? { height: 44, background: `linear-gradient(135deg, rgba(${DIARY_RGB},0.55), rgba(${DIARY_RGB},0.22))` } : {}),
                        opacity: editSaving || !editDraft.trim() ? 0.5 : 1,
                        cursor: editSaving || !editDraft.trim() ? "default" : "pointer",
                      }}>
                      {editSaving ? "Saving…" : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <HighlightedText
                  content={entry.content}
                  baseStyle={{
                    color: INK,
                    fontFamily: YFONT,
                    fontSize: 15,
                    lineHeight: 1.7,
                  }}
                />
              )}
              {/* Optional attachment — Creator recording, image, audio. */}
              {!isEditing && entry.attachment_url && (
                <div style={{ marginTop: 20 }} data-testid="entry-attachment">
                  {entry.attachment_kind === "video" || entry.attachment_kind === undefined ? (
                    <video
                      controls
                      src={entry.attachment_url}
                      style={{
                        width: "100%", borderRadius: 22, background: "var(--yuo-bg)",
                        boxShadow: `0 0 44px rgba(${DIARY_RGB},0.18) inset`,
                      }}
                    />
                  ) : entry.attachment_kind === "audio" ? (
                    <audio controls src={entry.attachment_url} style={{ width: "100%" }} />
                  ) : (
                    <img src={entry.attachment_url} alt=""
                      style={{ width: "100%", borderRadius: 22 }} />
                  )}
                </div>
              )}
            </div>

            {/* Continue writing capsule */}
            {!isEditing && (
              <div style={{
                padding: "20px 22px",
                borderRadius: 26,
                background: glow(DIARY_RGB, 0.10),
                marginBottom: 22,
                display: "flex", flexDirection: "column", gap: 12,
              }}>
                <div style={{
                  color: MUTED_36, fontSize: 9.5,
                  letterSpacing: "0.24em", textTransform: "uppercase",
                }}>Continue writing</div>
                <textarea
                  value={continueText}
                  onChange={(e) => setContinueText(e.target.value)}
                  placeholder="Add another thought…"
                  rows={4}
                  data-testid="continue-writing-input"
                  style={{
                    width: "100%",
                    background: "var(--fg-04)",
                    border: "none", outline: "none",
                    borderRadius: 20, padding: 16,
                    color: INK, fontFamily: YFONT,
                    fontSize: 14, lineHeight: 1.65, minHeight: 100,
                    boxShadow: `0 0 32px rgba(${DIARY_RGB},0.12) inset`,
                    resize: "vertical",
                  }}
                />
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button
                    onClick={() => setShowMyNotes(true)}
                    data-testid="diary-entry-notes-btn"
                    aria-label="Notes from the Library"
                    style={{
                      ...capsuleStyle({ active: false, size: "sm", room: "library" }),
                      color: ROOMS.library.accent,
                    }}>
                    <BookIcon color={ROOMS.library.hex} size={14} />
                    <span>Notes</span>
                  </button>
                  <button
                    onClick={saveContinued}
                    disabled={!continueText.trim() || saving}
                    data-testid="continue-save-btn"
                    style={{
                      ...capsuleStyle({ active: true, size: "md", room: "diary" }),
                      flex: 1,
                      opacity: !continueText.trim() || saving ? 0.5 : 1,
                      cursor: !continueText.trim() || saving ? "default" : "pointer",
                    }}>
                    {saving ? "…" : "Save"}
                  </button>
                </div>
              </div>
            )}

            {/* Reflections */}
            {entry.reflections && entry.reflections.length > 0 && (
              <div data-testid="entry-reflections" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {entry.reflections.map((r) => (
                  <ReflectionBlock
                    key={r.reflection_id}
                    reflection={r}
                    onAnswer={(q) => setAnswerFor({ reflection_id: r.reflection_id, question: q, draft: "" })}
                  />
                ))}
              </div>
            )}

            {/* Reflect action */}
            {!isEditing && (
              <div style={{ marginTop: 22, display: "flex", justifyContent: "center" }}>
                <button
                  onClick={runReflection}
                  disabled={reflecting}
                  data-testid="entry-reflect-btn"
                  style={{
                    ...capsuleStyle({ active: true, size: "lg", room: "diary" }),
                    opacity: reflecting ? 0.6 : 1,
                    cursor: reflecting ? "default" : "pointer",
                  }}>
                  <StoneOrb color="diary" size={14} />
                  <span>{reflecting ? "Reflecting…" : "Reflect with Mirror"}</span>
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Answer modal — glow surface, no old bright rectangle */}
      {answerFor && (
        <div
          onClick={() => setAnswerFor(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: "var(--dk-3410-72)", backdropFilter: "blur(14px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20,
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="answer-modal"
            style={{
              width: "100%", maxWidth: 560,
              background: `radial-gradient(120% 100% at 22% 0%, rgba(${DIARY_RGB},0.20), rgba(${DIARY_RGB},0.04) 62%, var(--yuo-bg) 100%)`,
              borderRadius: 28,
              padding: 24,
              boxShadow: `0 0 60px rgba(${DIARY_RGB},0.28)`,
              display: "flex", flexDirection: "column", gap: 14,
              animation: "yuo-emerge .55s cubic-bezier(.2,.7,.2,1) both",
            }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 10,
              color: MUTED_36, fontSize: 9.5,
              letterSpacing: "0.24em", textTransform: "uppercase",
            }}>
              <StoneOrb color="diary" size={12} />
              <span>Reflection · question</span>
            </div>
            <div style={{
              color: INK, fontFamily: YFONT,
              fontSize: 15, lineHeight: 1.65,
            }}>
              {answerFor.question}
            </div>
            <div style={{
              color: `rgba(${GROWTH_RGB},0.85)`, fontSize: 9.5,
              letterSpacing: "0.24em", textTransform: "uppercase",
            }}>Your answer</div>
            <textarea
              value={answerFor.draft}
              onChange={(e) => setAnswerFor({ ...answerFor, draft: e.target.value })}
              autoFocus
              rows={5}
              data-testid="answer-input"
              style={{
                width: "100%",
                background: "var(--fg-04)",
                border: "none", outline: "none",
                borderRadius: 20, padding: 16,
                color: INK, fontFamily: YFONT,
                fontSize: 14, lineHeight: 1.65,
                boxShadow: `0 0 30px rgba(${GROWTH_RGB},0.14) inset`,
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
              <button
                onClick={() => setAnswerFor(null)}
                style={capsuleStyle({ active: false, size: "sm" })}>
                Cancel
              </button>
              <button
                onClick={submitAnswer}
                disabled={!answerFor.draft.trim() || saving}
                data-testid="answer-submit"
                style={{
                  ...capsuleStyle({ active: true, size: "md", room: "symbiosis" }),
                  opacity: !answerFor.draft.trim() || saving ? 0.5 : 1,
                  cursor: !answerFor.draft.trim() || saving ? "default" : "pointer",
                }}>
                {saving ? "…" : "Save answer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <MyNotesSheet
        visible={showMyNotes}
        onClose={() => setShowMyNotes(false)}
        onInsert={insertToContinue}
        accentColor={ROOMS.diary.accent}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reflection block — a purple-glow capsule, no legacy borders
// ---------------------------------------------------------------------------
function ReflectionBlock({ reflection, onAnswer }) {
  const raw = reflection.text || reflection.content || "";
  const blocks = useMemo(() => splitReflectionIntoBlocks(raw), [raw]);
  return (
    <div style={{
      padding: "20px 22px",
      borderRadius: 26,
      background: glow(DIARY_RGB, 0.12),
      boxShadow: `0 0 34px rgba(${DIARY_RGB},0.10) inset`,
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        color: MUTED_36, fontSize: 9.5,
        letterSpacing: "0.24em", textTransform: "uppercase",
      }}>
        <StoneOrb color="diary" size={12} />
        <span>Mirror reflection · {new Date(reflection.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
      </div>
      {blocks.map((b, i) =>
        b.type === "question" ? (
          <div
            key={i}
            style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
            data-testid={`reflection-question-${reflection.reflection_id}-${i}`}>
            <button
              onClick={() => onAnswer(b.text)}
              data-testid={`answer-btn-${reflection.reflection_id}-${i}`}
              style={{
                ...capsuleStyle({ active: true, size: "sm", room: "symbiosis" }),
                flexShrink: 0,
                marginTop: 2,
              }}>
              answer
            </button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <HighlightedText
                content={b.text}
                baseStyle={{
                  color: INK,
                  fontFamily: YFONT,
                  fontSize: 14.5,
                  lineHeight: 1.65,
                }}
              />
            </div>
          </div>
        ) : (
          <div key={i} style={{ color: INK, fontFamily: YFONT, fontSize: 14.5, lineHeight: 1.65, whiteSpace: "pre-wrap" }}>
            <HighlightedText
              content={b.text}
              baseStyle={{
                color: INK,
                fontFamily: YFONT,
                fontSize: 14.5,
                lineHeight: 1.65,
              }}
            />
          </div>
        ),
      )}
    </div>
  );
}
